import React, { useState, useContext, useEffect, useCallback } from 'react';
import { catalogSVC as Product } from '@com/core/common/services/api';
import isEmpty from 'lodash/isEmpty';
import { reportError } from '@com/core/common/utils/sentry';
import Analytics from '@com/core/common/utils/analytics';
import {
    TProduct,
    TProductDetail,
    THit,
    TOffer,
    TFbtOffer,
    TGroupOptions,
    TVariant,
} from '@com/core/common/types/catalog';
import { TRecapiReccomendedProducts } from '@com/core/common/types/recommendation';
import { AppContext } from '@com/core/common/contexts/appContext';
import { NavContext } from '@com/core/common/contexts/navContext';
import { getOfferFromVariants, getProductUrl } from '@com/core/common/utils/product';
import { OptionTypeBase } from 'react-select';
import { CartContext } from '@com/core/common/contexts/cartContext';
import { MARKETPLACE } from '@com/core/common/baseConfig';
import { copyToClipboard } from '@com/core/common/utils/client';
import useTimer, { getDifference } from '@com/core/common/hooks/useTimer';
import { StoreContext } from '@com/core/common/contexts/storeContext';

const ENABLE_CUSTOM_NAVIGATION = ['kul', 'noon'].includes(MARKETPLACE);

type TProps = {
    children: JSX.Element;
    catalog: TProductDetail;
};

type TInterestedRecommendationType = { sku: string; recommendationToken: string };
interface IState {
    otherOffersQty?: { [offerCode: string]: number };
    product: TProduct;
    recommendations: TInterestedRecommendationType[];
    recommendationToken: string;
    recommendedProducts: THit[];
    sizingGuide: string;
    selectedAddons: string[];
    selectedQty: number;
    shareTooltip: boolean;
    isFouponSelected: boolean;
}

interface IContext extends IState {
    offer: TOffer | null;
    setOffer: (newOffer: TOffer) => void;
    getFBTItems: () => TFbtOffer[];
    getOtherOfferQty: (offerCode: string) => number;
    getProductSizingGuide: (sku: string) => void;
    setRecommendationSKU: (params: TInterestedRecommendationType) => void;
    getRecommendationToken: (sku: string) => string;
    getRecommendedProducts: () => void;
    getSelectedVariant: (options: TGroupOptions[]) => TGroupOptions;
    getSelectedSizeVariant: (options: TVariant[]) => TVariant;
    handleAddToCartClick: () => void;
    handleAddonsATCClick: () => void;
    handleAddonToggle: (offerCode: string) => void;
    handleOtherOffersQtyChange: (option: OptionTypeBase, offerCode: string | undefined) => void;
    handleQtyChange: (option: OptionTypeBase) => void;
    handleShareProductClick: () => void;
    navigateToProduct: (sku: string, offerCode?: string, url?: string) => void;
    isDealLocked: boolean;
    setIsDealLocked: (value: boolean) => void;
    isDealStarted: boolean;
    setIsDealStarted: (value: boolean) => void;
    setIsFouponSelected: (value: boolean) => void;
    catalogData: TProductDetail;
}

const initContext: IContext = {
    product: {} as TProduct,
    recommendations: [],
    recommendationToken: '',
    recommendedProducts: [],
    sizingGuide: '',
    selectedAddons: [],
    selectedQty: 1,
    shareTooltip: false,
    isFouponSelected: false,
    offer: null,
    isDealLocked: false,
    isDealStarted: false,
    setOffer: (): void => null,
    getFBTItems: () => [],
    getOtherOfferQty: () => 1,
    getProductSizingGuide: () => null,
    setRecommendationSKU: () => null,
    getRecommendationToken: (): string => '',
    getRecommendedProducts: () => null,
    getSelectedVariant: (): TGroupOptions => ({} as TGroupOptions),
    getSelectedSizeVariant: (): TVariant => ({} as TVariant),
    handleAddToCartClick: (): void => undefined,
    handleAddonsATCClick: (): void => undefined,
    handleAddonToggle: (): void => undefined,
    handleOtherOffersQtyChange: (): void => undefined,
    handleQtyChange: (): void => undefined,
    handleShareProductClick: (): void => undefined,
    navigateToProduct: (): void => undefined,
    setIsDealLocked: () => undefined,
    setIsDealStarted: () => undefined,
    setIsFouponSelected: () => undefined,
    catalogData: {} as TProductDetail,
};

export const ProductContext = React.createContext<IContext>(initContext);

export const ProductContextProvider = ({ catalog, children }: TProps): React.ReactElement => {
    const { isSlidingOptionsPanelOpen, toggleSlidingOptionsPanel } = useContext(AppContext);
    const { isLoading: isPageLoading } = useContext(NavContext);

    // if page is loading, force close all the sliding panels that are open
    useEffect(() => {
        if (isPageLoading) {
            Object.keys(isSlidingOptionsPanelOpen || {}).forEach(panelId => toggleSlidingOptionsPanel(panelId, true));
        }
        // DO NOT add `isSlidingOptionsPanelOpen` and `toggleSlidingOptionsPanel` as deps
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPageLoading]);

    const { storeCode } = useContext(StoreContext);
    const [product, setProduct] = useState(catalog.product);
    const [catalogData] = useState(catalog);
    const [offer, setOffer] = useState(getOfferFromVariants(catalog?.product?.variants, catalog?.product?.offer_code));
    const [recommendations, setRecommendations] = useState(initContext.recommendations);
    const [recommendationToken, setRecommendationToken] = useState(initContext.recommendationToken);
    const [recommendedProducts, setRecommendedProducts] = useState(initContext.recommendedProducts);
    const [selectedAddons, setSelectedAddons] = useState(initContext.selectedAddons);
    const [selectedQty, setSelectedQty] = useState(initContext.selectedQty);
    const [sizingGuide, setSizingGuide] = useState(initContext.sizingGuide);
    const [otherOffersQty, setOtherOffersQty] = useState(initContext.otherOffersQty);
    const [shareTooltip, setShareTooltip] = useState(initContext.shareTooltip);
    const [isFouponSelected, setIsFouponSelected] = useState(initContext.isFouponSelected);

    const { locale, webConfig } = useContext(AppContext);
    const { addAddonsToCart, addToCart, updateQuantity } = useContext(CartContext);
    const { navTo, setKeepLoading, setLoadingState } = useContext(NavContext);

    // #region - Timer
    const [isDealLocked, setIsDealLocked] = useState(!!getDifference(offer?.deal_starts));
    const [isDealStarted, setIsDealStarted] = useState(!!getDifference(offer?.deal_expires));

    const [, , , , dealUnlocked] = useTimer(offer?.deal_starts, true);
    const [, , , , dealExpired] = useTimer(offer?.deal_expires, true);

    useEffect(() => {
        setIsDealLocked(!dealUnlocked);
    }, [dealUnlocked]);

    useEffect(() => {
        setIsDealStarted(!dealExpired);
    }, [dealExpired]);

    // NOTE - this logic should be replicated in all components.
    // This does not work in the context - especially for Listing pages.
    // (still kept the above for the sake of details page)
    // -----------------------------------------
    // const [isDealLocked, setIsDealLocked] = useState(!!getDifference(offer?.deal_starts));
    // const [isDealStarted, setIsDealStarted] = useState(!!getDifference(offer?.deal_expires));

    // const [, , , , dealUnlocked] = useTimer(offer?.deal_starts, true);
    // const [, , , , dealExpired] = useTimer(offer?.deal_expires, true);

    // useEffect(() => {
    //     setIsDealLocked(!dealUnlocked);
    // }, [dealUnlocked]);

    // useEffect(() => {
    //     setIsDealStarted(!dealExpired);
    // }, [dealExpired]);
    // -----------------------------------------
    // #endregion

    useEffect(() => {
        if (ENABLE_CUSTOM_NAVIGATION) {
            // keep loading page till we hit the correct URL
            setKeepLoading(true);
        }
        return () => true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // set product data on catalog update
        setProduct(catalog?.product);

        if (ENABLE_CUSTOM_NAVIGATION) {
            // force load page till we hit the correct URL
            setLoadingState(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalog]);

    useEffect(() => {
        // set the offer data on product change
        setOffer(getOfferFromVariants(product?.variants, product?.offer_code));
        setSelectedAddons(getFBTItems().map(o => o.offer_code));
        getProductSizingGuide(product.sku);
        setSelectedQty(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product]);

    useEffect(() => {
        if (ENABLE_CUSTOM_NAVIGATION) {
            if (offer) {
                const desiredUrl = `${catalog.canonical_url}?o=${offer.offer_code}`;
                const desiredUrlWithLocale = `/${locale.seoLocale}${desiredUrl}`;
                const currentUrlWithLocale = (window?.location?.pathname || '') + (window?.location?.search || '');
                // only replace the URL if it does not match the canonical+offer format
                if (desiredUrlWithLocale !== currentUrlWithLocale) {
                    // append the offer_code to the URL
                    navTo(desiredUrl, {
                        replace: true,
                    });
                } else {
                    // force stop loading the page
                    setKeepLoading(false);
                    setLoadingState(false);
                }
            } else {
                // force stop loading the page
                setKeepLoading(false);
                setLoadingState(false);
            }
        }
        setIsDealLocked(!!getDifference(offer?.deal_starts));
        setIsDealStarted(!!getDifference(offer?.deal_expires));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offer]);

    const getProductSizingGuide = async (sku: string): Promise<void> => {
        try {
            const response = await Product(locale.apiLocale).getProductSizeGuideBySku(sku);
            if (response) {
                setSizingGuide(response.data);
            }
        } catch (error) {
            if (!(error.code === 400)) {
                reportError(error, { componentStack: 'ProductContextProvider.getProductSizingGuide' });
            }
        }
    };

    const getRecommendationToken = (sku: string): string => {
        const recommendation = recommendations.find((r: TInterestedRecommendationType) => r.sku === sku);
        return recommendation ? recommendation.recommendationToken : '';
    };

    const getRecommendedProducts = async (): Promise<void> => {
        let trackPageResult = null;
        try {
            trackPageResult = await Analytics.trackPage(locale, {
                event: 'page_detail',
                eventAction: product.sku,
                eventLabel: product.product_title,
                skuConfig: product.sku,
                offerId: product.offer_code,
                // store: catalog?.storeInfo?.storeCode,
            });
        } catch (error) {
            reportError(error, { componentStack: 'ProductContextProvider.getRecommendedProducts' });
        }

        // Return if preditions are disabled by webConfig
        const { recommendation: recommendationConfig } = webConfig;
        if (!recommendationConfig && !recommendationConfig.page_detail) {
            return;
        }

        //  Return if didn't get any predictions
        if (!trackPageResult || isEmpty(trackPageResult) || isEmpty(trackPageResult.results[0])) {
            return;
        }

        const { results } = trackPageResult;
        const recToken = trackPageResult.recommendationToken;

        const recommendedProductSkus = results.map((r: TRecapiReccomendedProducts['results'][0]) => r.catalogItemId);

        let recommendedProductsResponse = null;
        try {
            recommendedProductsResponse = await Product(locale.apiLocale).getProductDetailsForSkus(
                recommendedProductSkus.join(','),
            );
        } catch (error) {
            reportError(error, {
                componentStack: 'ProductContextProvider.getRecommendedProducts.getProductDetailsForSkus',
            });
        }

        if (
            !recommendedProductsResponse ||
            isEmpty(recommendedProductsResponse.data) ||
            isEmpty(recommendedProductsResponse.data.hits)
        ) {
            return;
        }
        const { hits } = recommendedProductsResponse && recommendedProductsResponse.data;
        setRecommendedProducts(hits);
        setRecommendationToken(recToken);
    };

    const getSelectedVariant = (variants: TGroupOptions[]): TGroupOptions =>
        variants.find(variant => variant.sku === offer?.sku_config) || variants[0];

    const getSelectedSizeVariant = (variants: TVariant[]): TVariant =>
        variants.find(variant => variant.offers.find(variantOffer => variantOffer.sku === offer?.sku)) ||
        ({} as TVariant);

    const setRecommendationSKU = (params: TInterestedRecommendationType): void => {
        const newRecommendations = recommendations;
        newRecommendations.push(params);
        setRecommendations(newRecommendations);
    };

    const handleAddToCartClick = (): void => {
        addToCart((offer as TOffer).offer_code, selectedQty, true, [], product.sku, isFouponSelected);
    };

    const handleQtyChange = (option: OptionTypeBase): void => {
        setSelectedQty(option.value);
    };

    const handleOtherOffersQtyChange = (option: OptionTypeBase, offerCode: string): void => {
        setOtherOffersQty({ ...otherOffersQty, [offerCode]: option.value });
    };

    const getOtherOfferQty = (offerCode: string) => otherOffersQty[offerCode] || 1;

    const navigateToProduct = (sku: string, offerCode = '', url = ''): void => {
        const storeParam = storeCode ? `store=${storeCode}` : '';

        navTo(getProductUrl({ sku, offer: { id: offerCode }, url }, true, storeParam));
    };

    // FBT STUFF
    const getFBTItems = useCallback(() => {
        if (!offer || product.fbt_offers.length === 0) return [];

        const currentOffer: TFbtOffer = {
            title: product.product_title,
            sku: product.sku,
            image_key: product.image_keys[0],
            offer_code: offer.offer_code,
            price: offer.price,
            sale_price: offer.sale_price,
            flags: offer.flags,
        };

        return [currentOffer, ...product.fbt_offers];
    }, [product, offer]);

    const handleAddonsATCClick = (): void => {
        const offerCodes = selectedAddons.join('/');
        const fbtItems = getFBTItems();
        const selectedFBTItem = fbtItems.filter(data => selectedAddons.includes(data.offer_code));
        addAddonsToCart(offerCodes, selectedFBTItem);
    };

    const handleAddonToggle = (offerCode: string): void => {
        const indexOfOffer = selectedAddons.indexOf(offerCode);
        const newSelection = selectedAddons;

        if (indexOfOffer === -1) {
            newSelection.push(offerCode);
        } else {
            newSelection.splice(indexOfOffer, 1);
        }

        setSelectedAddons([...newSelection]);
    };

    const handleShareProductClick = (): void => {
        if (navigator?.share !== undefined) {
            navigator
                .share({
                    title: product.product_title,
                    url: window.location.href,
                })
                .catch(error => {
                    reportError(error, { componentStack: 'ProductContextProvider.handleShareProduct' });
                });
        } else {
            copyToClipboard(
                window.location.href,
                () => {
                    setShareTooltip(true);
                    setTimeout(() => setShareTooltip(false), 2000);
                },
                error => {
                    reportError(error, { componentStack: 'ProductContextProvider.handleShareProduct.fallback' });
                },
            );
        }
    };

    const contextValue = {
        recommendationToken,
        offer,
        setOffer,
        product,
        catalogData,
        recommendations,
        recommendedProducts,
        selectedQty,
        selectedAddons,
        shareTooltip,
        isFouponSelected,
        sizingGuide,
        getFBTItems,
        getOtherOfferQty,
        getProductSizingGuide,
        setRecommendationSKU,
        getRecommendationToken,
        getRecommendedProducts,
        getSelectedVariant,
        getSelectedSizeVariant,
        handleAddToCartClick,
        handleAddonsATCClick,
        handleAddonToggle,
        handleOtherOffersQtyChange,
        handleQtyChange,
        handleShareProductClick,
        navigateToProduct,
        isDealLocked,
        setIsDealLocked,
        isDealStarted,
        setIsDealStarted,
        setIsFouponSelected,
    };

    return <ProductContext.Provider value={contextValue}>{children}</ProductContext.Provider>;
};
